import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  PencilIcon, TrashIcon, PlusIcon, DocumentIcon,
  CalendarIcon, UserGroupIcon, ClockIcon, CheckCircleIcon,
  BuildingOfficeIcon, FireIcon, TagIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid
} from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, CardHeader, CardTitle, Button, Avatar,
  StatusBadge, PriorityBadge, ProgressBar, PageLoader, EmptyState,
  Tabs, TabList, Tab, TabPanels, TabPanel,
  Modal, ModalBody, ModalFooter, Textarea, Badge, Select
} from '../../components/ui';
import { taskService } from '../../services';
import { useUIStore, useAuthStore } from '../../stores';
import { useForm } from 'react-hook-form';

dayjs.extend(relativeTime);

export default function TaskDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm } = useUIStore();
  const { user: currentUser } = useAuthStore();
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const { register: registerComment, handleSubmit: handleCommentSubmit, reset: resetComment } = useForm();

  // Check if user can update task status
  const canUpdateStatus = () => {
    if (!currentUser || !task || !project) return false;

    // Check if user is assignee
    const isAssignee = task.assignees?.some(
      assignee => assignee._id?.toString() === currentUser._id?.toString() ||
        assignee.toString() === currentUser._id?.toString()
    );

    // Check if user has canManageTasks permission
    const canManage = project.members?.some(member => {
      const memberUserId = member.user?._id?.toString() || member.user?.toString();
      const currentUserId = currentUser._id?.toString();
      return memberUserId === currentUserId && member.permissions?.canManageTasks === true;
    }) || project.owner?._id?.toString() === currentUser._id?.toString();

    return isAssignee || canManage;
  };

  useEffect(() => {
    fetchTask();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTask = async () => {
    try {
      const taskData = await taskService.getTask(id);
      setTask(taskData);

      // Project is already populated in the response
      if (taskData.project) {
        setProject(taskData.project);
      }
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('common.delete'),
      message: t('tasks.confirmDelete') || 'Are you sure you want to delete this task?',
      confirmText: t('common.delete'),
      variant: 'danger'
    });

    if (confirmed) {
      try {
        await taskService.deleteTask(id);
        toast.success(t('success.deleted'));
        navigate('/tasks');
      } catch (error) {
        toast.error(error.response?.data?.message || t('errors.serverError'));
      }
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target?.value || e;

    if (!canUpdateStatus()) {
      toast.error(t('common.forbidden') || 'You do not have permission to update this task');
      return;
    }

    if (newStatus === task.status) return;

    setUpdatingStatus(true);
    try {
      await taskService.updateTask(id, { status: newStatus });
      toast.success(t('success.updated'));
      fetchTask();
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const onCommentSubmit = async (data) => {
    try {
      await taskService.addComment(id, data.content);
      toast.success(t('success.created'));
      setShowCommentModal(false);
      resetComment();
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const toggleChecklistItem = async (itemId) => {
    try {
      await taskService.toggleChecklistItem(id, itemId);
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  if (!task) {
    return null;
  }

  const isOverdue = task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day') && task.status !== 'completed';
  const daysRemaining = task.dueDate ? dayjs(task.dueDate).diff(dayjs(), 'day') : null;

  return (
    <>
      <Helmet>
        <title>{task.title} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={task.title}
        backUrl="/tasks"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" icon={PencilIcon} onClick={() => navigate(`/tasks/${id}/edit`)}>
              {t('common.edit')}
            </Button>
            <Button variant="danger" icon={TrashIcon} onClick={handleDelete}>
              {t('common.delete')}
            </Button>
          </div>
        }
      />

      {/* Hero Section */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <h1 className="text-3xl font-bold text-neutral-dark truncate">{task.title}</h1>
                {canUpdateStatus() ? (
                  <Select
                    value={task.status}
                    onChange={handleStatusChange}
                    options={[
                      { value: 'todo', label: t('tasks.statuses.todo') || 'To Do' },
                      { value: 'in_progress', label: t('tasks.statuses.inProgress') || 'In Progress' },
                      { value: 'review', label: t('tasks.statuses.review') || 'Review' },
                      { value: 'completed', label: t('tasks.statuses.completed') || 'Completed' },
                      { value: 'cancelled', label: t('tasks.statuses.cancelled') || 'Cancelled' }
                    ]}
                    disabled={updatingStatus}
                    className="min-w-[140px]"
                  />
                ) : (
                  <StatusBadge status={task.status} />
                )}
                <PriorityBadge priority={task.priority} />
                {isOverdue && (
                  <Badge variant="danger">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    {t('tasks.overdue')}
                  </Badge>
                )}
              </div>

              {task.description && (
                <p className="text-gray-600 mb-4 line-clamp-2">{task.description}</p>
              )}

              <div className="flex flex-wrap items-center gap-6 text-sm">
                {task.project && (
                  <div className="flex items-center gap-2">
                    <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
                    <Link to={`/projects/${task.project._id}`} className="text-primary hover:underline font-medium">
                      {task.project.name}
                    </Link>
                  </div>
                )}
                {task.dueDate && (
                  <div className={`flex items-center gap-2 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                    <ClockIcon className="w-4 h-4" />
                    <span>
                      {t('tasks.dueDate')}: {dayjs(task.dueDate).format('MMM D, YYYY')}
                      {daysRemaining !== null && (
                        <span className="ml-2">
                          ({daysRemaining > 0 ? `${daysRemaining} days left` : 'Overdue'})
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {task.createdAt && (
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      Created {dayjs(task.createdAt).fromNow()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-shrink-0">
              {task.progress !== undefined && (
                <div className="p-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl border border-primary/20">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">{t('common.progress')}</p>
                    <div className="text-4xl font-bold text-primary mb-2">{task.progress}%</div>
                    <ProgressBar value={task.progress} color="primary" size="lg" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Main Content Tabs */}
      <Card>
        <CardBody>
          <Tabs>
            <TabList className="mb-6">
              <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                {t('common.overview')}
              </Tab>
              <Tab active={activeTab === 'comments'} onClick={() => setActiveTab('comments')}>
                {t('tasks.comments')} ({task.comments?.length || 0})
              </Tab>
              <Tab active={activeTab === 'checklist'} onClick={() => setActiveTab('checklist')}>
                {t('tasks.checklist')} ({task.checklist?.filter(i => i.isCompleted).length || 0}/{task.checklist?.length || 0})
              </Tab>
              <Tab active={activeTab === 'time'} onClick={() => setActiveTab('time')}>
                {t('tasks.timeEntries')} ({task.timeEntries?.length || 0})
              </Tab>
            </TabList>

            <TabPanels>
              {/* Overview Tab */}
              <TabPanel active={activeTab === 'overview'}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Task Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DocumentIcon className="w-5 h-5 text-primary" />
                        {t('tasks.taskDetails') || 'Task Information'}
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div className="space-y-4">
                        {task.project && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500 flex items-center gap-2">
                              <BuildingOfficeIcon className="w-4 h-4" />
                              {t('projects.title')}
                            </span>
                            <Link to={`/projects/${task.project._id}`} className="text-primary hover:underline font-medium">
                              {task.project.name}
                            </Link>
                          </div>
                        )}
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-500 flex items-center gap-2">
                            <CheckCircleIcon className="w-4 h-4" />
                            {t('common.status')}
                          </span>
                          <div className="flex items-center gap-2">
                            {canUpdateStatus() ? (
                              <Select
                                value={task.status}
                                onChange={handleStatusChange}
                                options={[
                                  { value: 'todo', label: t('tasks.statuses.todo') || 'To Do' },
                                  { value: 'in_progress', label: t('tasks.statuses.inProgress') || 'In Progress' },
                                  { value: 'review', label: t('tasks.statuses.review') || 'Review' },
                                  { value: 'completed', label: t('tasks.statuses.completed') || 'Completed' },
                                  { value: 'cancelled', label: t('tasks.statuses.cancelled') || 'Cancelled' }
                                ]}
                                disabled={updatingStatus}
                                className="min-w-[140px]"
                              />
                            ) : (
                              <StatusBadge status={task.status} />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-500 flex items-center gap-2">
                            <FireIcon className="w-4 h-4" />
                            {t('common.priority')}
                          </span>
                          <PriorityBadge priority={task.priority} />
                        </div>
                        {task.milestone && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('projects.milestones')}</span>
                            <span className="font-medium">{task.milestone.title}</span>
                          </div>
                        )}
                        {task.createdBy && (
                          <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-500">{t('common.createdBy') || 'Created by'}</span>
                            <div className="flex items-center gap-2">
                              <Avatar
                                src={task.createdBy.avatar ? (task.createdBy.avatar.startsWith('http') ? task.createdBy.avatar : process.env.REACT_APP_SOCKET_URL + task.createdBy.avatar) : null}
                                name={`${task.createdBy.firstName} ${task.createdBy.lastName}`}
                                size="xs"
                              />
                              <span className="font-medium">
                                {task.createdBy.firstName} {task.createdBy.lastName}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>

                  {/* Dates & Time */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CalendarIcon className="w-5 h-5 text-primary" />
                        {t('common.date')} & {t('tasks.time') || 'Time'}
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div className="space-y-4">
                        {task.startDate && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('projects.startDate')}</span>
                            <span className="font-medium">{dayjs(task.startDate).format('MMM D, YYYY')}</span>
                          </div>
                        )}
                        {task.dueDate && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('tasks.dueDate')}</span>
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isOverdue ? 'text-red-600' : ''}`}>
                                {dayjs(task.dueDate).format('MMM D, YYYY')}
                              </span>
                              {isOverdue && (
                                <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                          </div>
                        )}
                        {task.completedAt && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('tasks.completedAt') || 'Completed'}</span>
                            <span className="font-medium">{dayjs(task.completedAt).format('MMM D, YYYY h:mm A')}</span>
                          </div>
                        )}
                        {task.estimatedHours > 0 && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('tasks.estimatedHours')}</span>
                            <span className="font-medium">{task.estimatedHours}h</span>
                          </div>
                        )}
                        {task.actualHours > 0 && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('tasks.actualHours')}</span>
                            <span className="font-medium">{task.actualHours}h</span>
                          </div>
                        )}
                        {task.createdAt && (
                          <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-500">{t('common.createdAt')}</span>
                            <span className="font-medium">{dayjs(task.createdAt).format('MMM D, YYYY')}</span>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>

                  {/* Assignees */}
                  {task.assignees && task.assignees.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <UserGroupIcon className="w-5 h-5 text-primary" />
                          {t('tasks.assignees')} ({task.assignees.length})
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="space-y-3">
                          {task.assignees.map((assignee) => (
                            <div key={assignee._id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                              <div className="flex items-center gap-3">
                                <Avatar
                                  src={assignee.avatar ? (assignee.avatar.startsWith('http') ? assignee.avatar : process.env.REACT_APP_SOCKET_URL + assignee.avatar) : null}
                                  name={`${assignee.firstName} ${assignee.lastName}`}
                                  size="sm"
                                />
                                <div>
                                  <p className="font-medium text-sm">
                                    {assignee.firstName} {assignee.lastName}
                                  </p>
                                  {assignee.email && (
                                    <p className="text-xs text-gray-500">{assignee.email}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Tags */}
                  {task.tags && task.tags.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TagIcon className="w-5 h-5 text-primary" />
                          {t('common.tags')}
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="flex flex-wrap gap-2">
                          {task.tags.map((tag, index) => (
                            <Badge key={index} variant="neutral">{tag}</Badge>
                          ))}
                        </div>
                      </CardBody>
                    </Card>
                  )}
                </div>
              </TabPanel>

              {/* Comments Tab */}
              <TabPanel active={activeTab === 'comments'}>
                <div className="flex justify-end mb-4">
                  <Button size="sm" icon={PlusIcon} onClick={() => setShowCommentModal(true)}>
                    {t('tasks.addComment')}
                  </Button>
                </div>

                {task.comments && task.comments.length > 0 ? (
                  <div className="space-y-4">
                    {task.comments.slice().reverse().map((comment) => (
                      <div key={comment._id} className="p-4 bg-gray-50 rounded-lg border-l-4 border-primary">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar
                            src={comment.user?.avatar ? (comment.user.avatar.startsWith('http') ? comment.user.avatar : process.env.REACT_APP_SOCKET_URL + comment.user.avatar) : null}
                            name={`${comment.user?.firstName} ${comment.user?.lastName}`}
                            size="sm"
                          />
                          <div>
                            <p className="font-medium text-sm">
                              {comment.user?.firstName} {comment.user?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {dayjs(comment.createdAt).format('MMM D, YYYY h:mm A')}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title={t('tasks.noComments') || 'No comments yet'}
                    action={() => setShowCommentModal(true)}
                    actionLabel={t('tasks.addComment')}
                  />
                )}
              </TabPanel>

              {/* Checklist Tab */}
              <TabPanel active={activeTab === 'checklist'}>
                {task.checklist && task.checklist.length > 0 ? (
                  <div className="space-y-2">
                    {task.checklist.map((item) => (
                      <div
                        key={item._id}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                      >
                        <button
                          onClick={() => toggleChecklistItem(item._id)}
                          className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${item.isCompleted
                            ? 'bg-green-500 border-green-500'
                            : 'border-gray-300 hover:border-primary'
                            }`}
                        >
                          {item.isCompleted && (
                            <CheckCircleIconSolid className="w-4 h-4 text-white" />
                          )}
                        </button>
                        <div className="flex-1">
                          <p className={`text-sm ${item.isCompleted ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                            {item.text}
                          </p>
                          {item.completedAt && (
                            <p className="text-xs text-gray-500 mt-1">
                              Completed {dayjs(item.completedAt).fromNow()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState title={t('tasks.noChecklist') || 'No checklist items'} />
                )}
              </TabPanel>

              {/* Time Entries Tab */}
              <TabPanel active={activeTab === 'time'}>
                {task.timeEntries && task.timeEntries.length > 0 ? (
                  <div className="space-y-3">
                    {task.timeEntries.slice().reverse().map((entry) => (
                      <div key={entry._id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Avatar
                              src={entry.user?.avatar ? (entry.user.avatar.startsWith('http') ? entry.user.avatar : process.env.REACT_APP_SOCKET_URL + entry.user.avatar) : null}
                              name={`${entry.user?.firstName} ${entry.user?.lastName}`}
                              size="xs"
                            />
                            <span className="font-medium text-sm">
                              {entry.user?.firstName} {entry.user?.lastName}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-primary">
                            {entry.duration ? `${(entry.duration / 60).toFixed(1)}h` : '-'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {dayjs(entry.startTime).format('MMM D, YYYY h:mm A')}
                          {entry.endTime && ` - ${dayjs(entry.endTime).format('h:mm A')}`}
                        </div>
                        {entry.description && (
                          <p className="text-sm text-gray-500 mt-2">{entry.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState title={t('tasks.noTimeEntries') || 'No time entries'} />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      {/* Add Comment Modal */}
      <Modal isOpen={showCommentModal} onClose={() => setShowCommentModal(false)} title={t('tasks.addComment')}>
        <form onSubmit={handleCommentSubmit(onCommentSubmit)}>
          <ModalBody>
            <Textarea
              label={t('tasks.comment') || 'Comment'}
              rows={6}
              {...registerComment('content', { required: true })}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowCommentModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('common.add')}</Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
}